import { useState, useCallback } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import Iconify from '../../iconify';
import CustomTooltip from './CustomTooltip';
import { PresenceSegments } from '../types';

const calculateCompetitorsOnly = (totalVisibilityRate: number) => {
  return 100 - totalVisibilityRate;
};

export default function PresenceRateChart({
  data,
  round = 0,
  height = 6,
  totalVisibilityRate,
  colors: customColors,
}: {
  data: PresenceSegments;
  height?: number;
  totalVisibilityRate?: number;
  colors?: string[];
  round?: number;
}) {
  const defaultColors = [
    '#18A155',
    '#C09910',
    '#F9DE81',
    'rgba(145, 158, 171, 0.20)',
  ];

  const colors =
    customColors && customColors.length === 4
      ? customColors
      : defaultColors;

  const visibilityBase =
    typeof totalVisibilityRate === 'number' ? totalVisibilityRate : 100;

  const rawItems = [
    {
      label: 'Organic only',
      value: Math.max(0, data.organicOnly),
      color: colors[0],
      icon: 'solar:global-linear',
    },
    {
      label: 'Solo bidder',
      value: Math.max(0, data.soloBidder),
      color: colors[1],
      icon: 'carbon:user',
    },
    {
      label: 'Top ad position',
      value: Math.max(0, data.topAdPosition),
      color: colors[2],
      icon: 'carbon:user-multiple',
    },
    {
      label: 'Competitors only',
      value: Math.max(0, calculateCompetitorsOnly(visibilityBase)),
      color: colors[3],
      icon: 'ri:sword-line',
    },
  ];

  const items = rawItems.map((it) => ({
    ...it,
    value:
      round === 0 ? Math.round(it.value) : Number(it.value.toFixed(round)),
  }));

  // On ne garde que les segments visibles pour éviter les gaps fantômes
  const visibleItems = items.filter((it) => it.value > 0);

  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  return (
    <CustomTooltip
      placement="top"
      disableInteractive
      open={open}
      onClose={handleClose}
      title={
        <Stack direction="column" spacing={0.75} p={.5}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Presence breakdown
          </Typography>

          <Stack direction="column" spacing={0.5}>
            {items.map((it) => (
              <Stack
                key={it.label}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" gap={2.5} width={'100%'}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Iconify
                      icon={it.icon}
                      sx={{ width: 16, height: 16, color: it.color }}
                    />
                    <Typography variant="caption">
                      {it.label}
                    </Typography>
                  </Stack>

                  <Box flex={1} display="flex" justifyContent="flex-end">
                    <Typography variant="caption" color="text.secondary">
                      {it.value}%
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Stack>
      }
    >
      <Box
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        sx={{
          width: '100%',
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '2px',
            width: '100%',
            height: "12px",
            borderRadius: '2px',
            overflow: 'hidden',
          }}
        >
          {visibleItems.map((it) => (
            <Box
              key={it.label}
              sx={{
                flex: it.value,
                height: '100%',
                backgroundColor: it.color,
                transition: 'all 0.2s ease',
                borderRadius: '2px',
                minWidth: 2,
              }}
            />
          ))}
        </Box>
      </Box>
    </CustomTooltip>
  );
}